import CryptoJS from "crypto-js";
import { createTransform } from "redux-persist";

let aesKey = "";
let oldAesKey = "";

export function initialiseEncryption(config: {
  aesKey: string;
  oldAesKey?: string;
}): void {
  aesKey = config.aesKey ?? "";
  oldAesKey = config.oldAesKey ?? "";
}

/**
 * Mirrors the backend's normalizeKeyBuffer exactly.
 *
 * The backend uses Buffer.alloc(targetSize) which zero-fills, then copies the
 * key bytes in. crypto-js WordArrays don't auto-pad, so we replicate this by
 * extracting the raw bytes, padding/truncating into a fixed-size array, and
 * rebuilding a WordArray — producing the identical key the backend uses.
 */
function normalizeKeyWordArray(
  keyWordArray: CryptoJS.lib.WordArray,
): CryptoJS.lib.WordArray {
  const validKeySizes = [32, 24, 16];
  const keyLen = keyWordArray.sigBytes;

  let targetSize: number;
  if (validKeySizes.includes(keyLen)) {
    return keyWordArray; // already valid, use as-is (matches backend fast path)
  } else if (keyLen < 16) {
    targetSize = 16;
  } else if (keyLen < 24) {
    targetSize = 24;
  } else if (keyLen < 32) {
    targetSize = 32;
  } else {
    targetSize = 32; // truncate oversized keys
  }

  // Extract raw bytes from WordArray, pad/truncate to targetSize with zeros
  // (matches backend's Buffer.alloc(targetSize) which zero-fills by default)
  const srcWords = keyWordArray.words;
  const srcBytes: number[] = [];
  for (let i = 0; i < keyLen; i++) {
    srcBytes.push((srcWords[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff);
  }

  const paddedBytes = new Array(targetSize).fill(0);
  for (let i = 0; i < Math.min(keyLen, targetSize); i++) {
    paddedBytes[i] = srcBytes[i];
  }

  // Rebuild as WordArray
  const words: number[] = [];
  for (let i = 0; i < targetSize; i += 4) {
    words.push(
      ((paddedBytes[i] || 0) << 24) |
        ((paddedBytes[i + 1] || 0) << 16) |
        ((paddedBytes[i + 2] || 0) << 8) |
        (paddedBytes[i + 3] || 0),
    );
  }

  return CryptoJS.lib.WordArray.create(words, targetSize);
}

function generateIV(): CryptoJS.lib.WordArray {
  // CryptoJS.lib.WordArray.random() throws in Expo Go because it requires
  // crypto.getRandomValues which Expo Go does not provide.
  // We use getRandomValues via the global if available (custom builds),
  // otherwise fall back to a time+Math.random mix — safe for an IV since
  // an IV only needs to be unique, not secret.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) {
      bytes[i] = (Date.now() ^ (Math.random() * 256) ^ (i * 37)) & 0xff;
    }
  }
  const words: number[] = [];
  for (let i = 0; i < 16; i += 4) {
    words.push(
      (bytes[i] << 24) | (bytes[i + 1] << 16) | (bytes[i + 2] << 8) | bytes[i + 3],
    );
  }
  return CryptoJS.lib.WordArray.create(words, 16);
}

export function encryptString(data: string, oldString: boolean = false): string {
  try {
    const rawKey = CryptoJS.enc.Base64.parse(oldString ? oldAesKey : aesKey);
    const keyBytes = normalizeKeyWordArray(rawKey);
    // CryptoJS.lib.WordArray.random() relies on crypto.getRandomValues which is
    // unavailable in Expo Go. Generate the IV from Date.now() + Math.random()
    // entropy mixed with a counter — sufficient for a non-secret IV whose only
    // requirement is uniqueness per encryption, not secrecy.
    const ivBytes = generateIV();

    const encrypted = CryptoJS.AES.encrypt(data, keyBytes, {
      iv: ivBytes,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    // Wire format: "<iv_base64>:<ciphertext_base64>" — matches backend exactly
    const ivBase64 = CryptoJS.enc.Base64.stringify(ivBytes);
    return ivBase64 + ":" + encrypted.toString();
  } catch (error) {
    console.error("Encryption failed", error);
    throw new Error("Encryption failed");
  }
}

export function decryptString(encryptedData: string, oldString: boolean = false): string {
  try {
    const rawKey = CryptoJS.enc.Base64.parse(oldString ? oldAesKey : aesKey);
    const keyBytes = normalizeKeyWordArray(rawKey);

    const separatorIndex = encryptedData.indexOf(":");
    if (separatorIndex === -1) {
      throw new Error("Invalid encrypted data format — expected '<iv>:<ciphertext>'.");
    }

    const ivBytes = CryptoJS.enc.Base64.parse(encryptedData.slice(0, separatorIndex));
    const ciphertext = encryptedData.slice(separatorIndex + 1);

    const decrypted = CryptoJS.AES.decrypt(ciphertext, keyBytes, {
      iv: ivBytes,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    throw new Error("Decryption failed: " + JSON.stringify(error));
  }
}

export const persistEncryptionTransform = createTransform(
  (inboundState) => {
    try {
      return encryptString(JSON.stringify(inboundState));
    } catch (e) {
      console.error("Encryption failed", e);
      return inboundState;
    }
  },
  (outboundState) => {
    try {
      if (typeof outboundState !== "string") return {};
      const decrypted = decryptString(outboundState);
      return JSON.parse(decrypted);
    } catch (e) {
      console.error("Decryption or JSON parse failed", e);
      return {};
    }
  },
);
